import { dateSameDay } from 'common/display-state/display-time.js';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DATE_FORMATS, formatDate } from 'utilities/date.js';
import { getDocument, getSessionStorage, getWindow, getWindowLocation } from 'utilities/window.js';

const DAY_NAVIGATION_STORAGE_KEY = 'city-forecast:day-navigation';


// Within the website we use session storage to store the day navigation target instead of query params 
// to avoid polluting the address history & to make it as seamless as possible.
type DayNavigationStorage = {
  day: string;
  pathname: string;
};

type DayQueryParamResult = {
  index?: number;
  scrollSlug?: string;
};

const getDayNavigationStorage = (): Storage | undefined => {
  return getSessionStorage();
};

export const setDayNavigationTarget = ({ day, pathname }: DayNavigationStorage) => {
  try {
    getDayNavigationStorage()?.setItem(DAY_NAVIGATION_STORAGE_KEY, JSON.stringify({ day, pathname }));
   } catch {
     // Ignore storage failures; navigation still works without the handoff.
   }
};

const getDayNavigationTarget = (pathname: string) => {
  const storage = getDayNavigationStorage();
  const item = storage?.getItem(DAY_NAVIGATION_STORAGE_KEY);

  if (!item) {
    return undefined;
  }

  try {
    const target = JSON.parse(item) as DayNavigationStorage;
    if (target.pathname === pathname) {
      return target.day;
    }
  } catch {
    storage?.removeItem(DAY_NAVIGATION_STORAGE_KEY);
  }

  return undefined;
};

const clearDayNavigationTarget = () => {
  getDayNavigationStorage()?.removeItem(DAY_NAVIGATION_STORAGE_KEY);
};

export const getDayForecastSlug = (date: Date) => {
  return `forecast-${formatDate(date, DATE_FORMATS.DateParse)}`;
};

export const scrollToDayForecastSlug = (slug: string, offset: number) => {
  const target = getDocument()?.querySelector(`[data-slug="${slug}"]`) as HTMLElement | undefined;
  const lclWindow = getWindow();

  if (!target || !lclWindow) {
    return;
  }

  const { top } = target.getBoundingClientRect();
  lclWindow.scrollTo({ top: lclWindow.scrollY + top - offset, behavior: 'smooth' });
};


/** 
 * Returns forecast day index to open/select, plus optional mobile scroll target once that item is open.
 * Was initially using only a query param to determine the day to open, but now also checks session storage for a target day if no query param is present.
 * Used by the "Next 5 days" button on the 48-hour forecast page plus campaigns like Matariki / sports matches.
 * */
export const DayQueryParam = ({ days, hasSlug }: { days: { date: string }[]; hasSlug?: boolean }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParamLocation = searchParams.get('day');
  const [result, setResult] = useState<DayQueryParamResult>({});

  useEffect(() => {
    const storedDayNavigationTarget = getDayNavigationTarget(getWindowLocation().pathname);
    const dayParam = queryParamLocation ?? storedDayNavigationTarget;

    if (dayParam && days.length > 0) {
      let matchingIndex = -1;
      let targetDate: Date | undefined;
      // query param data ?day=2025-04-10 YYYY-MM-DD

      // if queryParamLocation is just an integer, we set that as the tab index (should be 1..n)
      const isInteger = /^\d+$/.test(dayParam);
      if (isInteger) {
        const tabIndex = parseInt(dayParam, 10);
        matchingIndex = tabIndex-1 < days.length ? tabIndex-1 : -1;
        targetDate = new Date(days[0].date); // we move to the start of the forecasts in mobile view
      } else {
        // otherwise we expect a 'yyyy-mm-66' format date to match
        const paramArgs = dayParam.split('-').map((dateStr) => parseInt(dateStr, 10));
        const queryParamDate = new Date(paramArgs[0], paramArgs[1] - 1, paramArgs[2]); // paramArgs[1] - 1 as month is 0 indexed
        targetDate = queryParamDate;
        matchingIndex = days.findIndex((day) => {
          // day.date "2025-05-05T12:00:00+12:00",
          const daysDate = new Date(day.date);
          return dateSameDay(queryParamDate, daysDate);
        });
      }

      if (storedDayNavigationTarget) {
        clearDayNavigationTarget();
      }

      if (matchingIndex < 0) {
        return;
      }

      if (queryParamLocation) {
        searchParams.delete('day');
        setSearchParams(searchParams, { replace: true });
      }

      setResult({
        index: matchingIndex,
        scrollSlug: hasSlug && targetDate ? getDayForecastSlug(targetDate) : undefined,
      });
    }
  }, [queryParamLocation, days, searchParams, setSearchParams, hasSlug]);

  return result;
};
